import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuItemConfirm,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { AdminUserDTO } from "@shaarp/common";
import { ChevronDownIcon, ExternalLinkIcon } from "lucide-react";
import { useState } from "react";
import { Button } from "../ui/button";
import { useToast, useToastError } from "../ui/use-toast";
import { DialogAdminUserAdvisor } from "./dialog-admin-user-advisor";
import { DialogAdminUserReferral } from "./dialog-admin-user-referral";

export default function DataTableCellAdminUserActions({
  user,
  onRefresh,
}: {
  user: AdminUserDTO;
  onRefresh: () => void;
}) {
  const { toast } = useToast();
  const toastError = useToastError();
  const api = useApi();
  const { t } = useTranslation();

  const [openAdvisorModal, setOpenAdvisorModal] = useState(false);
  const [openReferralModal, setOpenReferralModal] = useState(false);

  const hasReferralDiscounts =
    user.referralMonthlyDiscount !== null ||
    user.referralAnnualDiscount !== null;

  const doRemoveAdvisorFlag = async () => {
    try {
      await api.delete(`/admin/user/${user.id}/advisor`);
      user.advisorLicence = null;
      user.advisorMonthlyDiscount = null;
      user.advisorAnnualDiscount = null;

      toast({
        variant: "default",
        title: t("admin.dataTableAdminUsers.toast.advisorRemoved"),
        description: t(
          "admin.dataTableAdminUsers.toast.advisorRemovedDescription"
        ),
      });
      onRefresh();
    } catch (e) {
      toastError();
    }
  };

  const doAddAdvisorFlag = async () => {
    setOpenAdvisorModal(true);
  };

  const doEditAdvisorDiscounts = async () => {
    setOpenAdvisorModal(true);
  };

  const doRemoveReferralDiscounts = async () => {
    try {
      await api.delete(`/admin/user/${user.id}/referral`);
      user.referralMonthlyDiscount = null;
      user.referralAnnualDiscount = null;

      toast({
        variant: "default",
        title: t("admin.dataTableAdminUsers.toast.referralRemoved"),
        description: t(
          "admin.dataTableAdminUsers.toast.referralRemovedDescription"
        ),
      });
      onRefresh();
    } catch (e) {
      toastError();
    }
  };

  const doAddReferralDiscounts = async () => {
    setOpenReferralModal(true);
  };

  const doEditReferralDiscounts = async () => {
    setOpenReferralModal(true);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            {t("admin.dataTableAdminUsers.actions.actions")}
            <ChevronDownIcon className="ml-2" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {user.advisorLicence ? (
            <>
              <DropdownMenuItem onClick={doEditAdvisorDiscounts}>
                {t("admin.dataTableAdminUsers.actions.editAdvisor")}
              </DropdownMenuItem>
              <DropdownMenuItemConfirm
                text={t(
                  "admin.dataTableAdminUsers.actions.confirmRemoveAdvisor"
                )}
                onConfirm={doRemoveAdvisorFlag}
                className="text-destructive"
              >
                {t("admin.dataTableAdminUsers.actions.removeAdvisor")}
              </DropdownMenuItemConfirm>
            </>
          ) : (
            <DropdownMenuItem onClick={doAddAdvisorFlag}>
              {t("admin.dataTableAdminUsers.actions.addAdvisor")}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          {hasReferralDiscounts ? (
            <>
              <DropdownMenuItem onClick={doEditReferralDiscounts}>
                {t("admin.dataTableAdminUsers.actions.editReferral")}
              </DropdownMenuItem>
              <DropdownMenuItemConfirm
                text={t(
                  "admin.dataTableAdminUsers.actions.confirmRemoveReferral"
                )}
                onConfirm={doRemoveReferralDiscounts}
                className="text-destructive"
              >
                {t("admin.dataTableAdminUsers.actions.removeReferral")}
              </DropdownMenuItemConfirm>
            </>
          ) : (
            <DropdownMenuItem onClick={doAddReferralDiscounts}>
              {t("admin.dataTableAdminUsers.actions.addReferral")}
            </DropdownMenuItem>
          )}
          {import.meta.env.VITE_POSTHOG_PROJECT_ID && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() =>
                  window.open(
                    `https://eu.posthog.com/project/${import.meta.env.VITE_POSTHOG_PROJECT_ID}/person/${user.id}`,
                    "_blank"
                  )
                }
              >
                PostHog
                <ExternalLinkIcon className="ml-auto h-4 w-4" />
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {openAdvisorModal && (
        <DialogAdminUserAdvisor
          user={user}
          open={openAdvisorModal}
          onClose={() => setOpenAdvisorModal(false)}
          onSuccess={onRefresh}
        />
      )}

      {openReferralModal && (
        <DialogAdminUserReferral
          user={user}
          open={openReferralModal}
          onClose={() => setOpenReferralModal(false)}
          onSuccess={onRefresh}
        />
      )}
    </>
  );
}
